import type { ShapeAppraisalDocument } from "@aprao/model/src/shapes/ShapeAppraisal.js";
import type { ShapeSalesTaxConfiguration } from "@aprao/model/src/shapes/ShapeProject.js";
import * as Sentry from "@sentry/browser";
import { useState } from "react";
import useAsyncFn from "react-use/lib/useAsyncFn";

import { ApraoQueue, EvictError } from "../ApraoQueue.js";
import type { AnalysisOutputKind } from "./outputProducersRegistry.js";
import type {
  CloneableAnalysisParam,
  sensitivityWorker,
} from "./sensitivityWorker.js";

export const useSensitivityComputation = () => {
  // allow asynchronously calculating the variations of return on cost
  const [queue] = useState(
    () =>
      new ApraoQueue<typeof sensitivityWorker>({
        workerFactory: () =>
          new Worker(new URL("./sensitivityWorker.js", import.meta.url)),
        errorHandler: (error) => Sentry.captureException(error),
      }),
  );

  const sensitivityAsyncFnReturn = useAsyncFn(
    async (params: {
      appraisalDocument: ShapeAppraisalDocument;
      salesTaxConfiguration: ShapeSalesTaxConfiguration | null;
      analyses: ReadonlyArray<CloneableAnalysisParam<AnalysisOutputKind>>;
      steps: number;
    }) => {
      try {
        return await queue.submit({
          args: [params],
        });
      } catch (error) {
        if (EvictError.isEvictError(error)) {
          // Don't propagate eviction errors. They are an implementation detail,
          // and we cannot do anything with the info up the call stack.
          return;
        }

        throw error;
      }
    },
    [queue],
  );

  type SensitivityAsyncFnReturn = typeof sensitivityAsyncFnReturn;
  return sensitivityAsyncFnReturn as [
    SensitivityAsyncFnReturn[0],
    // This type override is to express that the returned callback's promise
    // should not be consumed. The useAsyncFn implementation messes with
    // it. For example, it "absorbs" any potential error so that `catch`
    // callbacks are never run.
    (...args: Parameters<SensitivityAsyncFnReturn[1]>) => void,
  ];
};
